/** AP-39 — Posteingang (`/inbox`). Zähler und Ablegen: `components/eingang/posteingang.ts`. */
import type { InboxValuesView } from '@symphenia/shared-types'
import { api } from '@/lib/axios'
import type { InboxItem, InboxItemDetail, InboxTarget, InboxTargetKind, ListInboxParams } from '../types/inbox.types'

export const inboxItemUrl = (id: string) => `/inbox/items/${id}`

export const fetchInbox = (params: ListInboxParams) =>
  api
    .get<{ data: InboxItem[]; meta: { total: number; page: number; limit: number; totalPages: number } }>('/inbox/items', { params })
    .then((r) => r.data)

export const fetchInboxItem = (id: string) => api.get<{ data: InboxItemDetail }>(inboxItemUrl(id)).then((r) => r.data.data)

/** Die Datei als Blob — für die Vorschau (PDF, Bild) und zum Herunterladen. */
export const fetchInboxContent = (id: string) => api.get<Blob>(`${inboxItemUrl(id)}/content`, { responseType: 'blob' }).then((r) => r.data)

export const deleteInboxItem = (id: string) => api.delete<{ data: { ids: string[] } }>(inboxItemUrl(id)).then((r) => r.data.data)

export const restoreInboxItem = (id: string) => api.post<{ data: InboxItemDetail }>(`${inboxItemUrl(id)}/restore`).then((r) => r.data.data)

export const setInboxIgnored = (id: string, ignored: boolean) =>
  api.patch<{ data: InboxItemDetail }>(inboxItemUrl(id), { ignored }).then((r) => r.data.data)

// ── Phase B: Ziele ─────────────────────────────────────────────────────────

export const fetchTargetKinds = () => api.get<{ data: InboxTargetKind[] }>('/inbox/targets/kinds').then((r) => r.data.data)

export const fetchTargets = (params: { q?: string; kind?: string; itemId?: string }) =>
  api.get<{ data: InboxTarget[] }>('/inbox/targets', { params }).then((r) => r.data.data)

export const assignInboxItem = (id: string, targetKind: string, targetId: string) =>
  api.post<{ data: InboxItemDetail }>(`${inboxItemUrl(id)}/assign`, { targetKind, targetId }).then((r) => r.data.data)

export const recognizeInboxItem = (id: string) => api.post<{ data: InboxItemDetail }>(`${inboxItemUrl(id)}/recognize`).then((r) => r.data.data)

// ── Phase H: Lesen mit KI (`INBOX.AI`) — beide starten einen Lauf im Hintergrund ──

export const readInboxItem = (id: string, ziel?: { targetKind: string; targetId: string }) =>
  api.post<{ data: InboxItemDetail }>(`${inboxItemUrl(id)}/read`, ziel ?? {}).then((r) => r.data.data)

export const classifyInboxItem = (id: string) => api.post<{ data: InboxItemDetail }>(`${inboxItemUrl(id)}/classify`).then((r) => r.data.data)

export const unassignInboxItem = (id: string, assignmentId: string) =>
  api.delete<{ data: InboxItemDetail }>(`${inboxItemUrl(id)}/assignments/${assignmentId}`).then((r) => r.data.data)

// ── Nachtrag 1: das Original mit pdf.js und die Positionen des zugeordneten Ziels ──

/** Die Datei als Bytes — für die Anzeige mit pdf.js (wie das Original der Eingangsrechnung). */
export async function fetchInboxOriginal(id: string): Promise<{ daten: ArrayBuffer; mimeType: string }> {
  const r = await api.get<ArrayBuffer>(`${inboxItemUrl(id)}/content`, { responseType: 'arraybuffer' })
  return { daten: r.data, mimeType: String(r.headers['content-type'] ?? '').split(';')[0] }
}

/** Je Ziel mit Werten (Preisanfrage, Bestellung) dessen Positionen: was dort gilt und was gelesen wurde. */
export const fetchInboxValues = (id: string) => api.get<{ data: InboxValuesView[] }>(`${inboxItemUrl(id)}/values`).then((r) => r.data.data)

export interface WerteWahl {
  targetKind: string
  targetId: string
  /** `readId` der gelesenen Zeilen */
  ids: string[]
  /** auch der gelesene Kopf (Nummer, Datum, Bindefrist) */
  head?: boolean
}

/** Gelesenes am Ziel übernehmen — mit den Rechten des Ziels. */
export const confirmInboxValues = (id: string, wahl: WerteWahl) => api.post<{ data: InboxValuesView[] }>(`${inboxItemUrl(id)}/values/confirm`, wahl).then((r) => r.data.data)

export const discardInboxValues = (id: string, wahl: WerteWahl) => api.post<{ data: InboxValuesView[] }>(`${inboxItemUrl(id)}/values/discard`, wahl).then((r) => r.data.data)
